import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ChatClientService } from 'stream-chat-angular';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  type Observable,
} from 'rxjs';
import { ApplicationUnreadService } from '../application-unread.service';
import { BadgeState, PushPlatform } from './push-platform';

/**
 * Keeps the app icon badge (installed app / home screen) equal to the unread
 * chats plus the unread applications while the app is open. While it is
 * closed, push-sw.js updates the badge from incoming pushes, starting from the
 * state stored here.
 */
@Injectable({ providedIn: 'root' })
export class AppBadgeService {
  private readonly platform = inject(PushPlatform);
  private readonly chatClient = inject(ChatClientService);
  private readonly applicationUnread = inject(ApplicationUnreadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly applicationCount$ = toObservable(
    this.applicationUnread.count
  );
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    combineLatest([this.chatUnread$(), this.applicationCount$])
      .pipe(
        map(([chat, applications]) => ({ chat, applications })),
        distinctUntilChanged(
          (a, b) => a.chat === b.chat && a.applications === b.applications
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(state => void this.apply(state));
  }

  /** Resets the badge and stored state, e.g. on logout. */
  async reset(): Promise<void> {
    await this.apply({ chat: 0, applications: 0 });
  }

  /** Waits for the chat connection, so the badge doesn't blink to 0 on start. */
  private chatUnread$(): Observable<number> {
    return this.chatClient.user$.pipe(
      filter(user => user !== undefined && user !== null),
      map(user => {
        const count = (user as { total_unread_count?: number })
          .total_unread_count;
        return typeof count === 'number' && count > 0 ? count : 0;
      })
    );
  }

  private async apply(state: BadgeState): Promise<void> {
    try {
      await this.platform.writeBadgeState(state);
      await this.platform.setBadge(state.chat + state.applications);
    } catch {
      // Badges are optional: unsupported, or not permitted yet on iOS.
    }
  }
}
